import React, { lazy, memo } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { CheckIcon, PaletteIcon, TypeIcon, ImageIcon, SparklesIcon, TargetIcon, TrendingUpIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export function BrandingService() {
  const features = [{
    icon: PaletteIcon,
    title: 'Logo Design',
    description: 'Unique, memorable logos that represent your brand'
  }, {
    icon: TypeIcon,
    title: 'Typography',
    description: 'Custom font selections that match your personality'
  }, {
    icon: ImageIcon,
    title: 'Brand Colours',
    description: 'Strategic colour palettes that evoke emotion'
  }, {
    icon: SparklesIcon,
    title: 'Style Guide',
    description: 'Comprehensive brand guidelines for consistency'
  }, {
    icon: TargetIcon,
    title: 'Brand Strategy',
    description: 'Clear positioning to stand out from competitors'
  }, {
    icon: TrendingUpIcon,
    title: 'Marketing Assets',
    description: 'Business cards, social media, and more'
  }];
  const benefits = ['Stand out from your competition', 'Build trust with customers', 'Create a memorable impression', 'Consistent brand experience', 'Professional brand materials', 'Scalable brand system'];
  const process = [{
    step: '01',
    title: 'Discovery',
    description: 'We learn about your business, values, and target audience to understand what makes you unique.'
  }, {
    step: '02',
    title: 'Research',
    description: 'We analyze your industry, competitors, and market trends to position your brand effectively.'
  }, {
    step: '03',
    title: 'Design',
    description: 'We create multiple brand concepts with logos, colors, and typography for your review.'
  }, {
    step: '04',
    title: 'Refine',
    description: 'We work with you to perfect your chosen direction with unlimited revisions included.'
  }, {
    step: '05',
    title: 'Deliver',
    description: 'You receive all brand files, guidelines, and assets ready to use across all platforms.'
  }];
  return <>
      <Helmet data-id="element-1951">
        <title data-id="element-1952">Brand Identity Design | Bright Pixxel Studio</title>
        <meta name="description" content="Brand identity design that makes your business memorable. Logos, colour systems and brand guidelines for UK small businesses. See your concept free." data-id="element-1953" />
        <link rel="canonical" href="https://bright-pixxel.com/services/branding" data-id="element-1954" />
        {/* Open Graph Meta Tags */}
        <meta property="og:locale" content="en_GB" data-id="element-1955" />
        <meta property="og:type" content="website" data-id="element-1956" />
        <meta property="og:title" content="Brand Identity Design | Bright Pixxel Studio" data-id="element-1957" />
        <meta property="og:description" content="Brand identity design that makes your business memorable. Logos, colour systems and brand guidelines for UK small businesses. See your concept free." data-id="element-1958" />
        <meta property="og:url" content="https://bright-pixxel.com/services/branding" data-id="element-1959" />
        <meta property="og:site_name" content="Bright Pixxel Studio" data-id="element-1960" />
        <meta property="og:image" content="https://uploadthingy.s3.us-west-1.amazonaws.com/qh74mEBb962nRk6qjvv9uT/Geometric_Logo_of_Bright_Pixxel_Studio.png" data-id="element-1961" />
        <meta property="og:image:width" content="1200" data-id="element-1962" />
        <meta property="og:image:height" content="630" data-id="element-1963" />
        <meta property="og:image:type" content="image/png" data-id="element-1964" />
        <meta property="og:image:alt" content="Bright Pixxel Studio - Brand Identity Design" data-id="element-1965" />
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" data-id="element-1966" />
        <meta name="twitter:title" content="Brand Identity Design | Bright Pixxel Studio" data-id="element-1967" />
        <meta name="twitter:description" content="Brand identity design that makes your business memorable. Logos, colour systems and brand guidelines for UK small businesses. See your concept free." data-id="element-1968" />
        <meta name="twitter:image" content="https://uploadthingy.s3.us-west-1.amazonaws.com/qh74mEBb962nRk6qjvv9uT/Geometric_Logo_of_Bright_Pixxel_Studio.png" data-id="element-1969" />
        <meta name="twitter:image:alt" content="Bright Pixxel Studio - Brand Identity Design" data-id="element-1970" />
      </Helmet>
      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-12 bg-gradient-to-b from-[#0C1B26] to-[#212C34]" data-id="element-1971">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1972">
          <div className="max-w-4xl mx-auto text-center" data-id="element-1973">
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5
          }} className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#0DC3DC]/20 flex items-center justify-center" data-id="element-1974">
              <PaletteIcon size={40} className="text-[#0DC3DC]" data-id="element-1975" />
            </motion.div>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-3xl md:text-5xl font-bold mb-6 text-white" data-id="element-1976">
              Brand Identity Design for UK Businesses
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="text-lg md:text-xl text-white/90 mb-8" data-id="element-1977">
              Create a memorable brand that resonates with your audience and
              sets you apart from the competition.
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1978">
              <Link to="/pricing" className="bg-[#0DC3DC] hover:bg-[#0DC3DC]/90 text-white px-8 py-3 rounded-lg font-medium transition-colors shadow-lg" data-id="element-1979">
                Get a Free Quote
              </Link>
              <Link to="/portfolio" className="bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-[#0C1B26] px-8 py-3 rounded-lg font-medium transition-colors" data-id="element-1980">
                See Our Work
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
      {/* Features Grid */}
      <section className="py-16 bg-white dark:bg-[#1E1E1E]" data-id="element-1981">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1982">
          <div className="max-w-6xl mx-auto" data-id="element-1983">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-center mb-12" data-id="element-1984">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-1985">
                Complete Brand Identity
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300" data-id="element-1986">
                Everything you need to build a strong, cohesive brand
              </p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1987">
              {features.map((feature, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="bg-[#F9FAFB] dark:bg-[#212C34] p-6 rounded-xl hover:shadow-lg transition-shadow" data-id="element-1988">
                  <div className="w-12 h-12 bg-[#0DC3DC]/30 rounded-lg flex items-center justify-center mb-4 border border-[#0DC3DC]/40" data-id="element-1989">
                    <feature.icon size={24} className="text-[#0DC3DC]" data-id="element-1990" />
                  </div>
                  <h3 className="text-xl font-bold mb-2" data-id="element-1991">{feature.title}</h3>
                  <p className="text-gray-600 dark:text-gray-300" data-id="element-1992">
                    {feature.description}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>
      {/* Benefits Section with Image */}
      <section className="py-16 bg-[#F9FAFB] dark:bg-[#121212]" data-id="element-1993">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1994">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-1995">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-1996">
              <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1997">
                Why Brand Identity Matters
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-8" data-id="element-1998">
                Your brand is more than just a logo. It's the complete
                experience your customers have with your business.
              </p>
              <div className="space-y-4" data-id="element-1999">
                {benefits.map((benefit, index) => <motion.div key={index} initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.5,
                delay: index * 0.1
              }} className="flex items-center gap-3" data-id="element-2000">
                    <div className="w-6 h-6 rounded-full bg-[#0DC3DC]/20 flex items-center justify-center flex-shrink-0" data-id="element-2001">
                      <CheckIcon size={14} className="text-[#0DC3DC]" data-id="element-2002" />
                    </div>
                    <span className="text-gray-700 dark:text-gray-300" data-id="element-2003">
                      {benefit}
                    </span>
                  </motion.div>)}
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="rounded-xl overflow-hidden shadow-xl" data-id="element-2004">
              <img src="https://images.unsplash.com/photo-1561070791-2526d30994b5?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="Brand identity design workspace with color swatches and logo sketches - Bright Pixxel Studio professional branding process" className="w-full h-auto" loading="lazy" decoding="async" data-id="element-2005" />
            </motion.div>
          </div>
        </div>
      </section>
      {/* Process Section */}
      <section className="py-16 bg-white dark:bg-[#1E1E1E]" data-id="element-2006">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2007">
          <div className="max-w-4xl mx-auto" data-id="element-2008">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-12 text-center" data-id="element-2009">
              Our Branding Process
            </motion.h2>
            <div className="space-y-8" data-id="element-2010">
              {process.map((item, index) => <motion.div key={index} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="flex gap-6" data-id="element-2011">
                  <div className="flex-shrink-0" data-id="element-2012">
                    <div className="w-16 h-16 rounded-full bg-[#0DC3DC]/20 flex items-center justify-center" data-id="element-2013">
                      <span className="text-2xl font-bold text-[#0DC3DC]" data-id="element-2014">
                        {item.step}
                      </span>
                    </div>
                  </div>
                  <div className="flex-grow" data-id="element-2015">
                    <h3 className="text-xl font-bold mb-2" data-id="element-2016">{item.title}</h3>
                    <p className="text-gray-600 dark:text-gray-300" data-id="element-2017">
                      {item.description}
                    </p>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-b from-[#0C1B26] to-[#212C34]" data-id="element-2018">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2019">
          <div className="max-w-3xl mx-auto text-center" data-id="element-2020">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-6 text-white" data-id="element-2021">
              Ready to Build Your Brand?
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-lg text-white/90 mb-8" data-id="element-2022">
              Let's create a brand identity that truly represents your business
              and resonates with your customers.
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} data-id="element-2023">
              <a href="/contact" className="inline-block bg-[#0DC3DC] hover:bg-[#0DC3DC]/90 text-[#0C1B26] px-8 py-4 rounded-full font-bold transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 text-lg" data-id="element-2024">
                Get your free homepage design
              </a>
            </motion.div>
          </div>
        </div>
      </section>
    </>;
}